import React, { createContext, useState, useEffect } from "react";
import componentPermission from "./permissions";
import { ConnectedProps, connect } from "react-redux";
import { UserAuthorization, SystemCredentials } from "us.common/services";
import decodeJWT from "jwt-decode";
import { AD, ADV1 } from "us.helper/azure";
import * as Actions from "us.common/actions";
import { Unauthorized, Loader } from "us.common/components";
import { withSelfCheck } from "./Functions";
import { i18n, initializeAppInsight } from "us.helper";
import Common from "us.common";
import { useAuth } from "us.helper/hooks/useAuth";
import { RootState } from "us.helper/types";

export const ConfigurationContext = createContext<any>([]);

const { systemCredentials } = Actions.SystemCredentialsAction;

type ConfigurationProviderProps = {
  setUserAuthorizationProfileSuccess: any;
  setUserAuthorizationProfileFail: any;
  configurationDetail: any;
  getUserProfilePhoto: any;
  getTenantDetails: any;
  children?: React.ReactNode;
};
type JWTToken = {
  [key: string]: string;
};

const ConfigurationProvider: React.FC<
  ConfigurationProviderProps & PropsFromRedux
> = (props) => {
  const {
    isAuthenticated,
    authResult,
    account,
    error,
    getV1Token,
    isInitialized,
  } = useAuth();
  const {
    setUserAuthorizationProfileSuccess,
    setUserAuthorizationProfileFail,
    configurationDetail,
    getUserProfilePhoto,
    getTenantDetails,
    changeLanguage,
  } = props;
  const [isLoading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (isAuthenticated && authResult && account) {
      getV1Token().then((v1AuthResult) => {
        if (v1AuthResult) {
          // Decode v1 token to initialize appInsight instance for the app
          const decodedV1Token = decodeJWT<JWTToken>(
            v1AuthResult.idToken || ""
          );
          ADV1.run(v1AuthResult, () => {
            getUserProfilePhoto(decodedV1Token?.id);
            getTenantDetails(decodedV1Token?.id);
          });
        } else {
          console.warn(
            "V1 token is not valid, skipping system credentials fetch."
          );
        }
      });
      //Decode access token to initialize appInsight instance for the app
      const decodedToken = decodeJWT<JWTToken>(authResult.accessToken);
      AD.run(authResult, decodedToken, () => {
        getSystemCredentials(authResult.uniqueId, decodedToken);
      });
    }
  }, [isAuthenticated, authResult, account, error]);

  const getSystemCredentials = async (userId: string, token: JWTToken) => {
    SystemCredentials.systemCredentials
      .get()
      .then(({ data, status }) => {
        if (status === 200) {
          AD.setCredentials(data, () => {
            initializeAppInsight(token, data);
            systemCredentials.success(data);
            getAuthorizationProfile(userId);
          });
        } else {
          setLoading(false);
          systemCredentials.fail(data);
        }
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const getAuthorizationProfile = async (userId: string) => {
    UserAuthorization.AuthorizationProfile.get(userId)
      .then(({ data, status }) => {
        sessionStorage.setItem("productVersion", data.productVersion);
        if (status === 200 && data?.featureList.length) {
          changeLanguageHandler(data?.preferredLanguage);
          setUserAuthorizationProfileSuccess(data);
        } else {
          setUserAuthorizationProfileFail([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching authorization profile:", error);
        setUserAuthorizationProfileFail([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   * @function
   * @description - Handle language change.
   * @param {string} lngCode - selected language.
   */
  const changeLanguageHandler = (lngCode: "no" | "en") => {
    switch (lngCode) {
      case "no":
        i18n.changeLanguage("no");
        changeLanguage(
          Common.Constants.languageCodes.nbNO,
          Common.Constants.currencyCodes.nbNO,
          Common.Constants.dateFormats.nbNO
        );
        break;
      case "en":
        i18n.changeLanguage("en");
        changeLanguage(
          Common.Constants.languageCodes.enGB,
          Common.Constants.currencyCodes.enGB,
          Common.Constants.dateFormats.enGB
        );
        break;
      default:
        i18n.changeLanguage("no");
        changeLanguage(
          Common.Constants.languageCodes.nbNO,
          Common.Constants.currencyCodes.nbNO,
          Common.Constants.dateFormats.nbNO
        );
        break;
    }
  };

  const showLoader = isLoading || !isInitialized; // ensure we never show Unauthorized before auth done
  const hasConfig = configurationDetail.length > 0;

  return (
    <>
      {showLoader && <Loader isDescriptionAvailable={true} />}
      {!showLoader && isAuthenticated && hasConfig && (
        <ConfigurationContext.Provider
          value={{
            configurationDetail,
            componentPermission,
          }}
        >
          {props.children}
        </ConfigurationContext.Provider>
      )}
      {!showLoader &&
        // Two Unauthorized scenarios:
        // 1. Auth resolved but user not authenticated
        // 2. Authenticated but configuration is empty (no features / authorization profile failed)
        (!isAuthenticated || (isAuthenticated && !hasConfig)) && (
          <Unauthorized />
        )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  configurationDetail: withSelfCheck(
    state.AuthorizationProfile.authorizationProfile.featureList
  ),
});

const { userAuthorizationProfile } = Actions.UserAuthorizationAction;
const { userProfilePhoto, tenantDetails } = Actions.UserInformationAction;
const mapDispatchToProps = {
  setUserAuthorizationProfileSuccess: userAuthorizationProfile.success,
  setUserAuthorizationProfileFail: userAuthorizationProfile.fail,
  getUserProfilePhoto: userProfilePhoto.get,
  getTenantDetails: tenantDetails.get,
  changeLanguage: Actions.changeLanguage,
  getSystemCredentials: systemCredentials.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ConfigurationProvider);
